import { useState, useEffect } from "react";
import { PieCharts, SlidePnl, shootToast } from "codekit";
import { useGetExecReportMutation, useGetExecAlertsMutation, useGetExecAlertsSlowMutation, useGetExecMovementMutation, useGenerateEjecutivoExcelMutation } from "../../../../../libs/redux/slices/Graficsx/Ingeprop/ingepropApiSlice";

const MONTHS = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const fmtM = (n) => "$ " + (n / 1_000_000).toLocaleString("es-CO", { maximumFractionDigits: 1 }) + "M";

const ExcKpis = ({ kpis }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${kpis.length}, 1fr)`, gap: 10, marginBottom: 20 }}>
    {kpis.map((k) => (
      <div key={k.label} style={{ background: "#F9FAFB", border: "1px solid #E6E6E6", borderRadius: 8, padding: "12px 14px" }}>
        <div style={{ fontSize: 9, color: "#64748B", textTransform: "uppercase", letterSpacing: ".05em", fontWeight: 700 }}>{k.label}</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: "#1B2F6E", marginTop: 6 }}>{k.value}</div>
      </div>
    ))}
  </div>
);

const MonthBar = ({ monthly }) => {
  const curYear = new Date().getFullYear();
  const byKey = {};
  monthly.forEach(({ YEARXX, MONTHX, VALOR }) => { byKey[`${YEARXX}-${MONTHX}`] = VALOR; });
  const maxVal = Math.max(...monthly.map((r) => r.VALOR), 1);
  const curMonth = new Date().getMonth() + 1;
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 180, paddingTop: 10 }}>
      {MONTHS.map((m, i) => {
        const mon = i + 1;
        const prev = byKey[`${curYear - 1}-${mon}`] ?? 0;
        const cur  = byKey[`${curYear}-${mon}`] ?? 0;
        const hPrev = Math.round((prev / maxVal) * 150);
        const hCur  = Math.round((cur / maxVal) * 150);
        const inProgress = mon === curMonth && cur > 0;
        return (
          <div key={m} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
            <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 155 }}>
              <div title={`${m} ${curYear - 1}: ${fmtM(prev)}`} style={{ width: 10, height: hPrev || 2, background: "#CBD5E1", borderRadius: "2px 2px 0 0", opacity: 0.8 }} />
              <div title={`${m} ${curYear}: ${fmtM(cur)}`} style={{ width: 10, height: hCur || 2, borderRadius: "2px 2px 0 0", background: inProgress ? "repeating-linear-gradient(45deg,#1B2F6E,#1B2F6E 3px,#2563B0 3px,#2563B0 6px)" : "#1B2F6E", opacity: cur > 0 ? 1 : 0.2 }} />
            </div>
            <div style={{ fontSize: 9, color: "#64748B", fontWeight: 600 }}>{m}</div>
          </div>
        );
      })}
    </div>
  );
};

const ParetoLt = ({ pareto }) => {
  const maxVal = pareto[0]?.INGPROPX ?? 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {pareto.map((p) => (
        <div key={p.CLIIDXXX} style={{ display: "grid", gridTemplateColumns: "1fr 200px 80px 60px", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#1B2F6E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.CLINOMXX}</div>
          <div style={{ background: "#F1F5F9", height: 16, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(p.INGPROPX / maxVal) * 100}%`, background: "linear-gradient(90deg,#1B2F6E,#2563B0)", borderRadius: 4 }} />
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, textAlign: "right", color: "#1B2F6E" }}>{p.INGPRESX}</div>
          <div style={{ fontSize: 11, fontWeight: 700, textAlign: "right", color: "#E8472A" }}>{p.PCT}%</div>
        </div>
      ))}
    </div>
  );
};

const COLOR_PALETTE = ["secondary", "success", "warning", "error", "accent", "primary", "info"];
const getGerColor   = (i) => {
  const base = COLOR_PALETTE[i % COLOR_PALETTE.length];
  return { colorCellChart: `${base}-500`, bgColorCards: `${base}-100`, iconColor: `${base}-400`, textColor: `${base}-600` };
};

const GerPie = ({ gerencias, onClickGer }) => {
  const total  = gerencias.reduce((acc, g) => acc + g.INGPROPX, 0);
  const data   = gerencias.map((g) => ({ name: g.CCODESXX, value: g.INGPROPX, date: `${g.PCT}%`, CCOIDXXX: g.CCOIDXXX }));
  const colors = gerencias.map((_, i) => getGerColor(i));
  const handleClick = (ctx) => { if (ctx?.data?.CCOIDXXX) { onClickGer(ctx.data); } };
  return (
    <>
      <div style={{ fontSize: 11, color: "#64748B", marginBottom: 8, fontStyle: "italic" }}>
        Clic en una gerencia (donut o leyenda) para ver el detalle en el Consolidado por cliente.
      </div>
      <PieCharts
        data={data}
        chartColors={colors}
        labelOne={fmtM(total)}
        labelTwo="Ingresos"
        isCurrency={true}
        onClickCellChart={handleClick}
        onClickCard={handleClick}
      />
    </>
  );
};

const SecHdr = ({ title, action }) => (
  <div style={{ fontSize: 13, fontWeight: 700, color: "#1B2F6E", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{ width: 4, height: 14, background: "#E8472A", borderRadius: 2, flexShrink: 0 }} />
      {title}
    </div>
    {action}
  </div>
);

const Section = ({ title, action, children }) => (
  <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #E6E6E6", padding: "18px 20px", marginBottom: 16 }}>
    <SecHdr title={title} action={action} />
    {children}
  </div>
);

const ChartLgd = () => (
  <div style={{ display: "flex", gap: 16, marginTop: 12, fontSize: 11, color: "#475569" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 10, background: "#CBD5E1", borderRadius: 2 }} /> Año anterior</div>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 10, background: "#1B2F6E", borderRadius: 2 }} /> Año actual</div>
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 12, height: 10, background: "repeating-linear-gradient(45deg,#1B2F6E,#1B2F6E 2px,#2563B0 2px,#2563B0 4px)", borderRadius: 2 }} /> Mes en curso</div>
  </div>
);

const ALERT_STYLES = {
  warning: { bg: "#FFFBEB", border: "#F59E0B", icon: "⚠️", text: "#92400E" },
  error:   { bg: "#FEF2F2", border: "#EF4444", icon: "📉", text: "#991B1B" },
  success: { bg: "#F0FDF4", border: "#22C55E", icon: "🟢", text: "#166534" },
};

const AlertBnd = ({ alerts, onDetail }) => {
  if (!alerts.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {alerts.map((a, i) => {
        const s = ALERT_STYLES[a.type] ?? ALERT_STYLES.warning;
        return (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 4px", borderBottom: i < alerts.length - 1 ? "1px solid #F1F5F9" : "none" }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: s.bg, border: `1.5px solid ${s.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>
              {s.icon}
            </div>
            <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: s.text, lineHeight: 1.5 }}>{a.msg}</span>
            <button
              onClick={() => onDetail(a.clients ?? [])}
              style={{ background: "none", border: "none", color: "#1B2F6E", fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", padding: "4px 8px", textDecoration: "underline" }}
            >
              Ver detalle
            </button>
          </div>
        );
      })}
    </div>
  );
};

const MOVER_COLORS = { up: "#16A34A", down: "#DC2626" };

const MoverRow = ({ item, dir }) => {
  const color  = MOVER_COLORS[dir];
  const arrow  = dir === "up" ? "▲" : "▼";
  const pctTxt = item.VARPCTXX !== null ? `${item.VARPCTXX > 0 ? "+" : ""}${item.VARPCTXX}%` : "Nuevo";
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 70px 60px", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: "1px solid #F1F5F9" }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#1B2F6E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.CLINOMXX}</div>
      <div style={{ fontSize: 11, textAlign: "right", color: "#475569" }}>{fmtM(item.INGCURR)}</div>
      <div style={{ fontSize: 11, fontWeight: 700, textAlign: "right", color }}>{arrow} {fmtM(Math.abs(item.VARIACIO))}</div>
      <div style={{ fontSize: 11, fontWeight: 700, textAlign: "right", color }}>{pctTxt}</div>
    </div>
  );
};

const MoverTbl = ({ movement }) => {
  const [tab, setTab] = useState("up");
  const items = movement[tab] ?? [];
  const tabStyle = (t) => ({
    padding: "5px 14px", fontSize: 11, fontWeight: 700, borderRadius: 20, cursor: "pointer", border: "none",
    background: tab === t ? (t === "up" ? "#DCFCE7" : "#FEE2E2") : "#F1F5F9",
    color: tab === t ? (t === "up" ? "#16A34A" : "#DC2626") : "#64748B",
  });
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <button style={tabStyle("up")}   onClick={() => setTab("up")}>▲ Top subidas</button>
        <button style={tabStyle("down")} onClick={() => setTab("down")}>▼ Top bajadas</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 90px 70px 60px", fontSize: 9, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", marginBottom: 4, padding: "0 0 4px", borderBottom: "2px solid #E2E8F0" }}>
        <span>Cliente</span><span style={{ textAlign: "right" }}>Actual</span><span style={{ textAlign: "right" }}>Variación</span><span style={{ textAlign: "right" }}>%</span>
      </div>
      {items.length ? items.map((it) => <MoverRow key={it.CLIIDXXX} item={it} dir={tab} />) : <div style={{ fontSize: 12, color: "#94A3B8", padding: "12px 0" }}>Sin datos para este periodo</div>}
    </div>
  );
};

const CliMovCol = ({ title, items, color, icon }) => (
  <div style={{ flex: 1, minWidth: 0 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 10 }}>{icon} {title} ({items.length})</div>
    {items.length === 0 && <div style={{ fontSize: 12, color: "#94A3B8" }}>Sin movimiento</div>}
    {items.map((c) => (
      <div key={c.CLIIDXXX} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: "1px solid #F1F5F9", gap: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#1B2F6E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.CLINOMXX}</div>
        <div style={{ fontSize: 11, fontWeight: 700, color, flexShrink: 0 }}>{fmtM(c.INGPROPX)}</div>
      </div>
    ))}
  </div>
);

const CliMov = ({ climovement }) => (
  <div style={{ display: "flex", gap: 24 }}>
    <CliMovCol title="Clientes nuevos"   items={climovement.new  ?? []} color="#16A34A" icon="✦" />
    <div style={{ width: 1, background: "#E2E8F0", flexShrink: 0 }} />
    <CliMovCol title="Clientes perdidos" items={climovement.lost ?? []} color="#DC2626" icon="✕" />
  </div>
);

/**
 * Panel pantalla completa - Informe Ejecutivo de Ingresos Propios
 * @param {boolean}  isOpen
 * @param {function} onClose
 * @param {object}   filters         - filtros activos del dashboard
 * @param {function} onOpenClients   - abre el consolidado por cliente
 */
const Paneject = ({ isOpen, onClose, filters, onOpenClients, onOpenGerencia }) => {
  const [kpis,          setKpis]          = useState([]);
  const [pareto,        setPareto]        = useState([]);
  const [gerencias,     setGerencias]     = useState([]);
  const [monthly,       setMonthly]       = useState([]);
  const [alerts,        setAlerts]        = useState([]);
  const [movement,        setMovement]        = useState({ up: [], down: [] });
  const [climovement,     setClimovement]     = useState({ new: [], lost: [] });
  const [isLoading,       setIsLoading]       = useState(false);
  const [isExporting,     setIsExporting]     = useState(false);
  const [isLoadingAlerts,   setIsLoadingAlerts]   = useState(false);
  const [isLoadingMovement, setIsLoadingMovement] = useState(false);
  const [getExecReport]          = useGetExecReportMutation();
  const [getExecAlerts]          = useGetExecAlertsMutation();
  const [getExecAlertsSlow]      = useGetExecAlertsSlowMutation();
  const [getExecMovement]        = useGetExecMovementMutation();
  const [generateEjecutivoExcel] = useGenerateEjecutivoExcelMutation();

  const handleExcel = () => {
    setIsExporting(true);
    generateEjecutivoExcel({ filters })
      .unwrap()
      .then(() => shootToast({ variant: "success", text: "Tu reporte se está generando! Recibirás confirmación por correo." }))
      .catch(() => shootToast({ variant: "error", text: "Error al generar el reporte. Intenta de nuevo." }))
      .finally(() => setIsExporting(false));
  };

  useEffect(() => {
    if (!isOpen) return;
    setIsLoading(true);
    setAlerts([]);
    setMovement({ up: [], down: [] });
    setClimovement({ new: [], lost: [] });
    getExecReport({ filters })
      .unwrap()
      .then((res) => {
        setKpis(res.kpis ?? []);
        setPareto(res.pareto ?? []);
        setGerencias(res.gerencias ?? []);
        setMonthly(res.monthly ?? []);
        setIsLoading(false);
        setIsLoadingAlerts(true);
        setIsLoadingMovement(true);
        Promise.all([
          getExecAlerts({ filters }).unwrap(),
          getExecAlertsSlow({ filters }).unwrap(),
        ])
          .then(([fastRes, slowRes]) => {
            setAlerts([...(fastRes.alerts ?? []), ...(slowRes.alerts ?? [])]);
          })
          .catch(() => {})
          .finally(() => setIsLoadingAlerts(false));
        return getExecMovement({ filters }).unwrap();
      })
      .then((res) => {
        setMovement(res.movement ?? { up: [], down: [] });
        setClimovement(res.climovement ?? { new: [], lost: [] });
      })
      .catch(() => {})
      .finally(() => setIsLoadingMovement(false));
  }, [isOpen, filters, getExecReport, getExecAlerts, getExecAlertsSlow, getExecMovement]);

  const subtitle = `YTD ${new Date().getFullYear()} · Para Gerencia General y Financiera`;

  return (
    <SlidePnl isOpen={isOpen} onClose={onClose} onBack={onClose} fullScreen={true} title="Informe Ejecutivo — Ingresos Propios" subtitle={subtitle} dotColor="#E8472A" isLoading={isLoading}>
      <div style={{ background: "#F5F6FA", minHeight: "100%" }}>
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "10px 22px 0" }}>
          <button
            onClick={handleExcel}
            disabled={isLoading || isExporting}
            style={{ background: "#E8472A", color: "#fff", border: "none", borderRadius: 6, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: isLoading || isExporting ? "not-allowed" : "pointer", opacity: isLoading || isExporting ? 0.6 : 1, display: "flex", alignItems: "center", gap: 6 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {isExporting ? "Generando..." : "Exportar Excel"}
          </button>
        </div>
        <div style={{ padding: "0 22px" }}>
          {(isLoadingAlerts || alerts.length > 0) && (
            <Section title="Alertas del periodo">
              {isLoadingAlerts
                ? <div style={{ fontSize: 12, color: "#64748B", padding: "10px 0" }}>Analizando alertas...</div>
                : <AlertBnd alerts={alerts} onDetail={onOpenClients} />
              }
            </Section>
          )}
          <Section title="Indicadores principales">
            <ExcKpis kpis={kpis} />
          </Section>
          <Section title="Evolución mensual — Ingresos Propios 12 meses">
            <MonthBar monthly={monthly} />
            <ChartLgd />
          </Section>
          <Section title="Top 10 clientes — Análisis Pareto">
            <ParetoLt pareto={pareto} />
          </Section>
          <Section title="Distribución por gerencia">
            <GerPie gerencias={gerencias} onClickGer={(data) => onOpenGerencia?.(data.CCOIDXXX, data.name)} />
          </Section>
          <Section title="Movimiento Top 10 — Variación de ingresos vs. año anterior">
            {isLoadingMovement
              ? <div style={{ fontSize: 12, color: "#64748B", padding: "10px 0" }}>Calculando variaciones...</div>
              : <MoverTbl movement={movement} />
            }
          </Section>
          <Section title="Movimiento de clientes vs. año anterior">
            {isLoadingMovement
              ? <div style={{ fontSize: 12, color: "#64748B", padding: "10px 0" }}>Calculando variaciones...</div>
              : <CliMov climovement={climovement} />
            }
          </Section>
        </div>
      </div>
    </SlidePnl>
  );
};

export default Paneject;
